import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Stack,
  Checkbox,
  FormControlLabel,
  Paper,
  Grid2,
} from "@mui/material";
import SettingsIcon from "@mui/icons-material/Settings";
import EditIcon from "@mui/icons-material/Edit";
import userAPI from "../../Services/Admin/userAPI";

const UserManagement = ({ onUserClick }) => {
  const [loading, setLoading] = useState(false);
  const usersList = useRef([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [speciality, setSpeciality] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [showColumns, setShowColumns] = useState(() => {
    const savedColumns = localStorage.getItem("showColumnsUsers");
    return savedColumns
      ? JSON.parse(savedColumns)
      : {
          id: true,
          email: true,
          role: true,
          firstname: true,
          lastname: true,
          createdAt: true,
          validatedAt: true,
          speciality: true,
          nbOfConnection: true,
          lastLoginDate: true,
        };
  });
  const [showDialog, setShowDialog] = useState(false);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const data = await userAPI.getUsers(); // API à adapter
      console.log(data);
      usersList.current = data;
      updateFilteredUsers();
    } catch (error) {
      console.error("Erreur lors de la récupération des utilisateurs :", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    localStorage.setItem("showColumnsUsers", JSON.stringify(showColumns));
  }, [showColumns]);

  useEffect(() => {
    updateFilteredUsers();
  }, [searchTerm, speciality]);

  const updateFilteredUsers = () => {
    const filteredList = usersList.current.filter((user) => {
      const matchesSearchTerm = user.email
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
      const matchesSpeciality =
        speciality === "" || user.speciality === speciality;
      return matchesSearchTerm && matchesSpeciality;
    });
    setFilteredUsers(filteredList);
  };

  const handleSpecialityChange = (event) => setSpeciality(event.target.value);
  const handleSearchChange = (event) => setSearchTerm(event.target.value);

  const handleShowColumnsDialog = () => setShowDialog(true);
  const handleCloseDialog = () => setShowDialog(false);

  const handleColumnChange = (column) => {
    setShowColumns({
      ...showColumns,
      [column]: !showColumns[column],
    });
  };

  return (
    <Card sx={{ maxWidth: 1200, margin: "auto", padding: { xs: 0, md: 2 } }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Gestion des Utilisateurs
        </Typography>
        <Grid2 container spacing={2} mb={3}>
          <Grid2 size={{ xs: 12, md: 4 }}>
            <FormControl fullWidth>
              <InputLabel>Spécialité</InputLabel>
              <Select value={speciality} onChange={handleSpecialityChange}>
                <MenuItem value="">Toutes</MenuItem>
                <MenuItem value="Orthopédiste">Orthopédiste</MenuItem>
                <MenuItem value="Cardiologue">Cardiologue</MenuItem>
                {/* Ajoute d'autres spécialités ici */}
              </Select>
            </FormControl>
          </Grid2>
          <Grid2 size={{ xs: 12, md: 8 }}>
            {" "}
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Rechercher par email"
              value={searchTerm}
              onChange={handleSearchChange}
            />
          </Grid2>
        </Grid2>
        <Box display="flex" justifyContent="space-between" mb={3}>
          <Stack direction="row" spacing={2}>
            <Button variant="contained" color="primary">
              + Ajouter un utilisateur
            </Button>
            <IconButton
              variant="outlined"
              color="secondary"
              onClick={handleShowColumnsDialog}
            >
              <SettingsIcon />
            </IconButton>
          </Stack>
        </Box>

        <Dialog open={showDialog} onClose={handleCloseDialog}>
          <DialogTitle>Afficher les colonnes</DialogTitle>
          <DialogContent>
            <Box display="flex" flexDirection="column" gap={2}>
              {Object.keys(showColumns).map((column) => (
                <FormControlLabel
                  key={column}
                  control={
                    <Checkbox
                      checked={showColumns[column]}
                      onChange={() => handleColumnChange(column)}
                    />
                  }
                  label={
                    column.charAt(0).toUpperCase() +
                    column.slice(1).replace(/([A-Z])/g, " $1")
                  }
                />
              ))}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} color="primary">
              Fermer
            </Button>
          </DialogActions>
        </Dialog>

        <Paper sx={{ width: "100%", overflow: "hidden" }}>
          <TableContainer>
            <Table stickyHeader aria-label="sticky table">
              <TableHead>
                <TableRow>
                  <TableCell>Action</TableCell>
                  {showColumns.id && <TableCell>ID</TableCell>}
                  {showColumns.email && <TableCell>Email</TableCell>}
                  {showColumns.role && <TableCell>Rôle</TableCell>}
                  {showColumns.firstname && <TableCell>Prénom</TableCell>}
                  {showColumns.lastname && <TableCell>Nom</TableCell>}
                  {showColumns.createdAt && <TableCell>Créé le</TableCell>}
                  {showColumns.validatedAt && <TableCell>Validé le</TableCell>}
                  {showColumns.speciality && <TableCell>Spécialité</TableCell>}
                  {showColumns.nbOfConnection && (
                    <TableCell>Connexions</TableCell>
                  )}
                  {showColumns.lastLoginDate && (
                    <TableCell>Dernière connexion</TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredUsers.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>
                      <IconButton onClick={() => onUserClick(user.id)}>
                        <EditIcon />
                      </IconButton>
                    </TableCell>
                    {showColumns.id && <TableCell>{user.id}</TableCell>}
                    {showColumns.email && <TableCell>{user.email}</TableCell>}
                    {showColumns.role && (
                      <TableCell>{user.role.join(", ")}</TableCell>
                    )}
                    {showColumns.firstname && (
                      <TableCell>{user.firstname}</TableCell>
                    )}
                    {showColumns.lastname && (
                      <TableCell>{user.lastname}</TableCell>
                    )}
                    {showColumns.createdAt && (
                      <TableCell>{user.createdAt}</TableCell>
                    )}
                    {showColumns.validatedAt && (
                      <TableCell>{user.validatedAt}</TableCell>
                    )}
                    {showColumns.speciality && (
                      <TableCell>{user.speciality}</TableCell>
                    )}
                    {showColumns.nbOfConnection && (
                      <TableCell>{user.nbOfConnection}</TableCell>
                    )}
                    {showColumns.lastLoginDate && (
                      <TableCell>{user.lastLoginDate}</TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </CardContent>
    </Card>
  );
};

export default UserManagement;
